import api, { handleResponse, handleError } from './api';

const walletService = {
  /**
   * Get the current user's wallet
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getWallet() {
    try {
      const response = await api.get('/wallets/me');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get wallet with recent transactions
   * @param {number} transactionLimit - Number of transactions to include
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getWalletSummary(transactionLimit = 10) {
    try {
      const response = await api.get(`/wallets/me/summary?transactionLimit=${transactionLimit}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get transaction history
   * @param {number} skip - Number of transactions to skip
   * @param {number} take - Number of transactions to return
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getTransactions(skip = 0, take = 20) {
    try {
      const response = await api.get(`/wallets/me/transactions?skip=${skip}&take=${take}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Check if user has sufficient balance
   * @param {number} amount - Amount to check in P$ cents
   * @returns {Promise<{success: boolean, data: {sufficient: boolean, requiredAmount: number}|null, error: string|null}>}
   */
  async checkBalance(amount) {
    try {
      const response = await api.get(`/wallets/me/check-balance?amount=${amount}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Top up the wallet with P$ (legacy - direct top-up without payment)
   * @param {number} amount - Amount in P$ cents (100 = P$1.00)
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async topUp(amount) {
    try {
      const response = await api.post('/wallets/me/top-up', { amount });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a Stripe Checkout session for topping up the wallet
   * @param {number} amount - Amount in P$ cents (100 = P$1.00)
   * @returns {Promise<{success: boolean, data: {sessionId: string, url: string}|null, error: string|null}>}
   */
  async createCheckoutSession(amount) {
    try {
      const response = await api.post('/wallets/me/top-up/checkout', { amount });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a Stripe PaymentIntent for embedded wallet top-up
   * @param {number} amount - Amount in P$ cents (100 = P$1.00)
   * @returns {Promise<{success: boolean, data: {clientSecret: string, paymentIntentId: string}|null, error: string|null}>}
   */
  async createPaymentIntent(amount) {
    try {
      const response = await api.post('/wallets/me/top-up/payment-intent', { amount });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Format P$ amount for display
   * @param {number} cents - Amount in cents
   * @returns {string} - Formatted string like "P$10.00"
   */
  formatAmount(cents) {
    const dollars = cents / 100;
    return `P$${dollars.toFixed(2)}`;
  },

  /**
   * Format P$ amount with sign for display
   * @param {number} cents - Amount in cents (can be negative)
   * @returns {string} - Formatted string like "+P$10.00" or "-P$5.00"
   */
  formatAmountWithSign(cents) {
    const sign = cents >= 0 ? '+' : '';
    return `${sign}${this.formatAmount(Math.abs(cents))}`;
  },
};

export default walletService;
